import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormSpy, useFormState } from 'react-final-form';
import { ARRAY_ERROR } from 'final-form';

import { getActEventsBlock } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmEventSubmittedDocumentsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmEventSubmittedDocumentsForm';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';

import { ErknmActEventItem } from './ErknmActEventItem';
import styles from './ErknmActEvents.module.scss';
import { useDebouncedValue } from '../../../../../../hooks/useDebouncedMemo';
import { IKnmStatus } from '../../../../../../APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmErpStatusCodesEnum } from '../../../../../../APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';
import { IKnmErknmFormSuspensionEvent } from '../../../../../MainModule/interfaces/IKnmErknmFormSuspensionEvent';

interface IErknmActsItemProps {
  id: string;
  isDisabledField: boolean;
  name: string;
  actIndex?: number;
  isDecision?: boolean;
  isPm?: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isPaperless: boolean | undefined;
  value: IKnmErknmFormAct | IKnmPmFormAct | IDecisionActForm;
  knmStatus?: IKnmStatus | undefined;
}

export const ErknmActEvents = ({
  id,
  name,
  formRef,
  isDisabledField,
  actIndex,
  isDecision,
  isPm,
  isPaperless,
  value,
  knmStatus,
}: IErknmActsItemProps) => {
  const push = formRef?.current.mutators.push;

  let eventsFieldName = 'eventsErknm';
  const suspensionEventsFieldName = 'suspensionEvents';

  if (isDecision) {
    eventsFieldName = 'events';
  }

  if (isPm) {
    eventsFieldName = 'eventsPm';
  }

  const { values } = useFormState({
    subscription: { values: true },
  });

  // Перечень действий
  const eventsDebounced = useDebouncedValue<IKnmErknmFormEvent[]>(values?.[eventsFieldName] || [], 1000);
  const suspensionEventsDebounced = useDebouncedValue<IKnmErknmFormSuspensionEvent[]>(
    values?.[suspensionEventsFieldName] || [],
    1000,
  );

  // Перечень осуществленных действий в акте
  const innerEvents: IKnmErknmFormEvent[] | undefined = useMemo(() => value?.events || [], [value]);

  const selectedKnmEventTypes = useMemo(() => eventsDebounced?.filter(x => x.type)?.map(x => x.type), [
    eventsDebounced,
  ]);

  const [uniqKnmEventTypesSelectOptions, setUniqKnmEventTypesSelectOptions] = useState<
    IDictionaryEventTypeSelectOption[] | undefined
    // @ts-ignore
  >(selectedKnmEventTypes);

  const eventsBlockId = getActEventsBlock(id);

  // Список невыбранных типов действий
  const typeOptions = useMemo(() => {
    return uniqKnmEventTypesSelectOptions?.filter(i => !innerEvents.find(j => j.type?.title === i.label)) || [];
  }, [uniqKnmEventTypesSelectOptions, innerEvents]);

  const requestForDocumentsEvents = useMemo(
    () => eventsDebounced?.filter(i => i.type?.title && i.type?.title.indexOf('Истребование документов') > -1),
    [JSON.stringify(eventsDebounced)],
  );

  const allSubmittedDocuments = useMemo(() => {
    const submittedDocumentsResult: IKnmErknmEventSubmittedDocumentsForm[] = [];

    requestForDocumentsEvents?.forEach(i => {
      i.submittedDocuments?.forEach(doc => {
        submittedDocumentsResult.push(doc);
      });
    });

    return submittedDocumentsResult;
  }, [JSON.stringify(eventsDebounced)]);

  // Список истребованных документов из "Перечня действий"
  const autoInnerEventForAdding = useMemo(() => {
    const autoInnerEventForAddingMap = new Map<string, IKnmErknmFormEvent>();

    if (
      knmStatus?.code === KnmErpStatusCodesEnum.TYPE_WAITING_CARRY_OUT ||
      knmStatus?.code === KnmErpStatusCodesEnum.TYPE_CARRIED_OUT
    ) {
      suspensionEventsDebounced.forEach(item => {
        const meta: IKnmErknmFormEvent | undefined = eventsDebounced.find(
          el => el.type?.label === item.event?.type?.label,
        );

        const key = meta?.type?.label;

        if (meta && key && !autoInnerEventForAddingMap.has(key)) {
          autoInnerEventForAddingMap.set(key, meta);
        }
      });
    }

    // Сохраняем порядок действий из "Перечня действий" (eventsDebounced),
    // т.к. suspensionEventsDebounced может идти в другом порядке
    return Array.from(autoInnerEventForAddingMap.values()).sort(
      (a, b) => eventsDebounced.indexOf(a) - eventsDebounced.indexOf(b),
    );
  }, [JSON.stringify(eventsDebounced), JSON.stringify(suspensionEventsDebounced), knmStatus?.code]);

  // Есть ли в акте действие "Истребование документов"?
  const hasInnerDocumentEvent = useMemo(() => {
    return innerEvents?.find(e => e?.type?.label && e?.type?.label.indexOf('Истребование документов') > -1);
  }, [innerEvents]);

  // Есть ли представленные документы в действиях
  const hasSubmittedDocuments = useMemo(() => {
    let isCanNotBeDeleted = false;

    allSubmittedDocuments.forEach(document => {
      if (['SD-1', 'SD-3'].includes(document?.status?.code || '')) {
        isCanNotBeDeleted = true;
      }
    });

    return isCanNotBeDeleted;
  }, [allSubmittedDocuments]);

  /** Автоматическое добавление действий в акт:
   * 1. действия из приостановок ("Перечень действий");
   * 2. "Истребование документов" при наличии представленных документов.
   * Оба сценария объединены, чтобы исключить дублирование одного и того же типа действия.
   */
  useEffect(() => {
    if (!push) {
      return;
    }

    const addedTypeLabels = new Set<string>();
    const isDocumentLabel = (label?: string) => !!label && label.indexOf('Истребование документов') > -1;

    // Можно ли добавить "Истребование документов" с представленными документами
    const canAddDocumentEvent =
      !!allSubmittedDocuments?.length && !hasInnerDocumentEvent && hasSubmittedDocuments && !isDisabledField;

    const pushAutoEvent = (option: IDictionaryEventTypeSelectOption, withDocuments: boolean) => {
      if (!option?.label || addedTypeLabels.has(option.label)) {
        return;
      }

      const originalEvent = withDocuments
        ? requestForDocumentsEvents?.find(
            (e: IKnmErknmFormEvent) => e.type?.value?.dictVersionId === option.value?.dictVersionId,
          )
        : undefined;

      addedTypeLabels.add(option.label);

      push(`${name}.events`, {
        guid: undefined,
        type: option,
        startDate: undefined,
        stopDate: undefined,
        dateCreate: undefined,
        inspectors: undefined,
        submittedDocuments: originalEvent?.submittedDocuments,
        protocol: {
          file: undefined,
          appendicesToTheProtocolOfAction: [],
          studiedDocuments: withDocuments ? originalEvent?.submittedDocuments : undefined,
        },
        listId: uuidv4(),
        isNew: true,
        autoInnerEventForAddingFlag: true,
      });
    };

    // 1. Действия из приостановок ("Перечень действий")
    autoInnerEventForAdding.forEach(item => {
      const meta = typeOptions?.find(e => item?.type?.label && e?.label && e?.label.indexOf(item?.type?.label) > -1);

      if (meta) {
        // "Истребование документов" добавляем с документами, если выполнены условия
        pushAutoEvent(meta, isDocumentLabel(meta.label) && canAddDocumentEvent);
      }
    });

    // 2. "Истребование документов" при наличии представленных документов (если ещё не добавлено выше)
    if (canAddDocumentEvent) {
      typeOptions?.filter(e => isDocumentLabel(e?.label)).forEach(option => pushAutoEvent(option, true));
    }
  }, [autoInnerEventForAdding, allSubmittedDocuments, hasInnerDocumentEvent, hasSubmittedDocuments]);

  const addEvent = useCallback(() => {
    if (!isDisabledField && push) {
      const addingEvent: IKnmErknmFormEvent = {
        guid: undefined,
        type: undefined,
        startDate: undefined,
        stopDate: undefined,
        dateCreate: undefined,
        inspectors: undefined,
        submittedDocuments: undefined,
        listId: uuidv4(),
        isNew: true,
      };

      push(`${name}.events`, addingEvent);
    }
  }, [formRef, push, isDisabledField]);

  useEffect(() => {
    if (selectedKnmEventTypes?.length && uniqKnmEventTypesSelectOptions?.length !== selectedKnmEventTypes?.length) {
      // @ts-ignore
      setUniqKnmEventTypesSelectOptions(selectedKnmEventTypes);
    }
  }, [uniqKnmEventTypesSelectOptions?.length, selectedKnmEventTypes?.length]);

  return (
    <FieldBlock
      id={eventsBlockId}
      margin="top"
      label={getTitleFieldWithRedStar({
        label: 'Перечень осуществленных действий',
        field: `${name}.events`,
        isPm,
        isDecision,
        isKnm: !isPm && !isDecision,
      })}
      className={styles.GroupMarginBottom}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          disabled={isDisabledField}
          onClick={() => addEvent()}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (
            !isDecision &&
            actIndex !== undefined &&
            props.errors?.acts &&
            props.errors?.acts[actIndex]?.events?.[ARRAY_ERROR]
          ) {
            const fieldError = props.errors?.acts[actIndex]?.events?.[ARRAY_ERROR];
            const errorMessage: string | undefined = typeof fieldError === 'string' ? fieldError : undefined;
            const isFieldTouched = props.touched?.[`acts[${actIndex}].events`];

            if (isFieldTouched && errorMessage) {
              return (
                <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>Поле не может быть пустым</p>
              );
            }
          }

          if (!isDecision && actIndex === undefined && props.errors?.act && props.errors?.act?.events?.[ARRAY_ERROR]) {
            const fieldError = props.errors?.act?.events?.[ARRAY_ERROR];
            const errorMessage: string | undefined = typeof fieldError === 'string' ? fieldError : undefined;
            const isFieldTouched = props.touched?.[`act.events`];

            if (isFieldTouched && errorMessage) {
              return (
                <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>Поле не может быть пустым</p>
              );
            }
          }

          if (isDecision && props.errors?.organization?.act && props.errors?.organization?.act?.events) {
            const fieldError = props.errors?.organization?.act?.events?.[ARRAY_ERROR];
            const errorMessage: string | undefined = typeof fieldError === 'string' ? fieldError : undefined;
            const isFieldTouched = props.touched?.[`organization.act.events`];

            if (isFieldTouched && errorMessage) {
              return (
                <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>Поле не может быть пустым</p>
              );
            }
          }

          return null;
        }}
      </FormSpy>
      <ErknmActEventItem
        name={`${name}.events`}
        actLevelName={name}
        watchOnly={isDisabledField}
        uniqKnmEventTypesSelectOptions={uniqKnmEventTypesSelectOptions}
        setUniqKnmEventTypesSelectOptions={setUniqKnmEventTypesSelectOptions}
        formRef={formRef}
        // @ts-ignore
        knmEventTypesSelectOptions={selectedKnmEventTypes}
        actId={id}
        actIndex={actIndex}
        isDecision={isDecision}
        isPm={isPm}
        isPaperless={isPaperless}
        value={value}
      />
    </FieldBlock>
  );
};
